import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Alert, AlertDescription, LanguageSelector } from "@tellescope/ui";
import { Card } from "@tellescope/design-system";
import { ExternalLink } from "lucide-react";
import { useOrganization } from "../../shared/store/organizationStore";
import { useEnduserAcceptTerms } from "../store/hooks";

export const TermsAcceptancePage: React.FC = () => {
  const { t } = useTranslation("auth");
  const organization = useOrganization();
  const acceptTerms = useEnduserAcceptTerms();

  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Collect all policy links
  const policies: { title: string; url: string }[] = [];

  if (organization?.customTermsOfService) {
    policies.push({
      title: t("Terms of Service"),
      url: organization.customTermsOfService,
    });
  }

  if (organization?.customPrivacyPolicy) {
    policies.push({
      title: t("Privacy Policy"),
      url: organization.customPrivacyPolicy,
    });
  }

  if (organization?.customPolicies) {
    for (const policy of organization.customPolicies) {
      policies.push({
        title: policy.title,
        url: policy.url,
      });
    }
  }

  const handleAccept = useCallback(async () => {
    if (!agreed) return;

    setIsLoading(true);
    setError(null);

    const success = await acceptTerms();

    if (!success) {
      setError(t("Failed to accept terms. Please try again."));
    }

    setIsLoading(false);
  }, [agreed, acceptTerms, t]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card variant="floating" padding="root" className="w-full max-w-md">
        <Card.Header className="text-center">
          {organization?.logoURL && (
            <div className="flex justify-center mb-4">
              <img
                src={organization.logoURL}
                alt={organization.name || "Organization logo"}
                className="h-16 max-w-[200px] object-contain"
              />
            </div>
          )}
          <Card.Title className="text-2xl font-bold">{t("Terms of Service")}</Card.Title>
          <Card.Description>{t("Please review and accept our policies to continue")}</Card.Description>
        </Card.Header>

        <Card.Body className="space-y-4">
          {/* Policy Links */}
          <div className="space-y-2">
            {policies.map((policy, index) => (
              <a
                key={index}
                href={policy.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                {policy.title}
              </a>
            ))}
          </div>

          {/* Agreement Checkbox */}
          <div className="flex items-start space-x-3 pt-4">
            <Checkbox id="agree-terms" checked={agreed} onCheckedChange={(checked) => setAgreed(checked === true)} />
            <label htmlFor="agree-terms" className="text-sm leading-tight cursor-pointer">
              {t("I have read and agree to the above policies")}
            </label>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </Card.Body>

        <Card.Footer className="flex flex-col gap-3">
          <Button onClick={handleAccept} disabled={!agreed || isLoading} className="w-full">
            {isLoading ? t("Accepting...") : t("Continue")}
          </Button>

          <div className="w-full flex justify-center pt-2">
            <LanguageSelector />
          </div>
        </Card.Footer>
      </Card>
    </div>
  );
};
